import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Edit3, Trash2, Save, X, ChevronDown } from 'lucide-react';
import type { Trend, TrendExample } from '@/types';

interface EditableTrendCardProps {
  trend: Trend;
  trendIndex: number;
  isEditing: boolean;
  isDeleted: boolean;
  onEdit: (index: number, field: 'name' | 'description', value: string) => void;
  onDelete: (index: number) => void;
  editedExamples: TrendExample[];
  onDeleteExample: (trendIndex: number, exampleId: string) => void;
  onOpenLightbox: (eventId: string, companySlug: string) => void;
}

const MAX_NAME_LENGTH = 200;
const MAX_DESCRIPTION_LENGTH = 500;

export function EditableTrendCard({
  trend,
  trendIndex,
  isEditing,
  isDeleted,
  onEdit,
  onDelete,
  editedExamples,
  onDeleteExample,
  onOpenLightbox
}: EditableTrendCardProps) {
  const [editingField, setEditingField] = useState<'name' | 'description' | null>(null);
  const [editValue, setEditValue] = useState('');
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  // Don't render if deleted
  if (isDeleted) {
    return null;
  }

  const handleStartEdit = (field: 'name' | 'description') => {
    setEditingField(field);
    setEditValue(trend[field]);
  };

  const handleSaveEdit = () => {
    if (editingField && editValue.trim() !== trend[editingField]) {
      onEdit(trendIndex, editingField, editValue.trim());
    }
    setEditingField(null);
    setEditValue('');
  };

  const handleCancelEdit = () => {
    setEditingField(null);
    setEditValue('');
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      handleCancelEdit();
    }
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleSaveEdit();
    }
  };

  const maxLength = editingField === 'name' ? MAX_NAME_LENGTH : MAX_DESCRIPTION_LENGTH;
  const isOverLimit = editValue.length > maxLength;

  return (
    <>
      <Collapsible defaultOpen={true}>
        <Card>
          <CollapsibleTrigger asChild>
            <CardHeader className="cursor-pointer hover:bg-gray-50 transition-colors w-full">
              <div className="flex items-center justify-between">
                <div className="flex-1 text-left">
                  {editingField === 'name' ? (
                    <div className="space-y-2" onClick={(e) => e.stopPropagation()}>
                      <Textarea
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onKeyDown={handleKeyDown}
                        className={`w-full text-base font-semibold ${isOverLimit ? 'border-red-500' : ''}`}
                        placeholder="Trend name"
                        rows={2}
                        autoFocus
                      />
                      <div className="flex items-center justify-between">
                        <span className={`text-xs ${isOverLimit ? 'text-red-600' : 'text-gray-500'}`}>
                          {editValue.length} / {maxLength} characters
                        </span>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={handleCancelEdit}
                          >
                            <X className="h-4 w-4 mr-1" />
                            Cancel
                          </Button>
                          <Button
                            size="sm"
                            onClick={handleSaveEdit}
                            disabled={isOverLimit || !editValue.trim()}
                          >
                            <Save className="h-4 w-4 mr-1" />
                            Save
                          </Button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <CardTitle className="text-base">{trend.name}</CardTitle>
                  )}
                </div>

                {isEditing && editingField !== 'name' && (
                  <div className="flex items-center gap-2 ml-4" onClick={(e) => e.stopPropagation()}>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleStartEdit('name')}
                      title="Edit name"
                    >
                      <Edit3 className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setShowDeleteDialog(true)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      title="Delete trend"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                )}

                {!isEditing && (
                  <ChevronDown className="h-5 w-5 text-gray-500 transition-transform duration-200 data-[state=open]:rotate-180 ml-4" />
                )}
              </div>
            </CardHeader>
          </CollapsibleTrigger>

          <CollapsibleContent>
            <CardContent>
              {/* Description */}
              {editingField === 'description' ? (
                <div className="space-y-2 mb-4">
                  <Textarea
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    onKeyDown={handleKeyDown}
                    className={`w-full ${isOverLimit ? 'border-red-500' : ''}`}
                    placeholder="Trend description"
                    rows={4}
                    autoFocus
                  />
                  <div className="flex items-center justify-between">
                    <span className={`text-xs ${isOverLimit ? 'text-red-600' : 'text-gray-500'}`}>
                      {editValue.length} / {maxLength} characters
                    </span>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={handleCancelEdit}
                      >
                        <X className="h-4 w-4 mr-1" />
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        onClick={handleSaveEdit}
                        disabled={isOverLimit || !editValue.trim()}
                      >
                        <Save className="h-4 w-4 mr-1" />
                        Save
                      </Button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="mb-4">
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-gray-700 flex-1">{trend.description}</p>
                    {isEditing && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleStartEdit('description')}
                        title="Edit description"
                        className="flex-shrink-0"
                      >
                        <Edit3 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              )}

              {/* Summary */}
              {trend.summary && (
                <p className="text-sm text-gray-600 italic mb-4">{trend.summary}</p>
              )}

              {/* Examples */}
              {editedExamples && editedExamples.length > 0 && (
                <div className="mt-4">
                  <h4 className="text-sm font-semibold text-gray-900 mb-3">Examples:</h4>
                  <div className="space-y-2">
                    {editedExamples.map((example, exIdx) => (
                      <div
                        key={example.id}
                        className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer group"
                        onClick={() => {
                          if (!isEditing) {
                            onOpenLightbox(example.id, example.slug);
                          }
                        }}
                      >
                        <div className="flex-shrink-0 w-6 h-6 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center text-xs font-medium">
                          {exIdx + 1}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium text-blue-600">
                              {example.slug}
                            </span>
                            {example.relevanceScore && (
                              <span className="text-xs text-gray-500">
                                ({example.relevanceScore.toFixed(1)}/10)
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-700">{example.hook}</p>
                        </div>
                        {isEditing && editedExamples.length > 1 && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={(e) => {
                              e.stopPropagation();
                              onDeleteExample(trendIndex, example.id);
                            }}
                            className="opacity-0 group-hover:opacity-100 transition-opacity text-red-600 hover:text-red-700 hover:bg-red-50"
                            title="Delete example"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                  {isEditing && editedExamples.length === 1 && (
                    <p className="text-xs text-gray-500 mt-2 italic">
                      This is the last example. Trends must have at least one example.
                    </p>
                  )}
                </div>
              )}
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Trend?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{trend.name}"? This will remove the trend and all {editedExamples.length} associated {editedExamples.length === 1 ? 'example' : 'examples'}. This action cannot be undone until you save.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                onDelete(trendIndex);
                setShowDeleteDialog(false);
              }}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete Trend
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
